import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ChevronRight, Play, Shield, Target, Heart, Zap, 
  Award, Users, Star, ArrowRight, Check, ChevronDown
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import LegalFooter from "./legal/LegalFooter";
import PublicNavbar from "./PublicNavbar";

const HomePage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTestimonial, setActiveTestimonial] = useState(0);
  const videoRef = useRef(null);

  // Redirect logged-in users to dashboard if they have access
  useEffect(() => {
    if (user) {
      const isAdminOrCoach = user?.role === "admin" || user?.role === "coach";
      const subscriptionTier = user?.subscription_tier;
      const hasActiveSubscription = subscriptionTier && 
        subscriptionTier !== "free" && 
        subscriptionTier !== "none" &&
        subscriptionTier !== "";
      
      if (isAdminOrCoach || hasActiveSubscription) {
        navigate("/dashboard");
      }
    }
  }, [user, navigate]);

  const testimonials = [
    {
      name: "Marcus Chen",
      role: "Collegiate Athlete",
      quote: "Paladin transformed my training. I added 40lbs to my squat in 3 months.",
      avatar: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop"
    },
    {
      name: "Sarah Williams",
      role: "CrossFit Competitor",
      quote: "The AI coaching is like having a world-class trainer in my pocket 24/7.",
      avatar: "https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100&h=100&fit=crop"
    },
    {
      name: "David Park",
      role: "Professional Basketball",
      quote: "My vertical jump increased by 6 inches. The programming is next level.",
      avatar: "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?w=100&h=100&fit=crop"
    }
  ];

  const programs = [
    {
      title: "FOUNDATIONS",
      subtitle: "Build Your Base",
      price: "$20",
      interval: "/week",
      features: ["Full workout library", "AI workout generator", "Progress tracking", "Basic analytics"],
      popular: false,
      tier: "foundations"
    },
    {
      title: "ACCELERATOR",
      subtitle: "Maximize Performance",
      price: "$40",
      interval: "/week",
      features: ["Everything in Foundations", "AI Coach chatbot", "Advanced analytics", "Custom workout plans", "Priority support"],
      popular: true,
      tier: "accelerator"
    },
    {
      title: "ELITE",
      subtitle: "World-Class Training",
      price: "$849",
      interval: "/month",
      features: ["Everything in Accelerator", "AI Form analysis", "1-on-1 coaching access", "Personalized nutrition", "VIP support"],
      popular: false,
      tier: "elite"
    }
  ];

  const keyComponents = [
    {
      icon: Target,
      title: "ATHLETICISM",
      description: "Data-driven approach to improving vertical jump, sprint speed, lateral quickness, and explosive power."
    },
    {
      icon: Shield,
      title: "DURABILITY",
      description: "Injury prevention through strength conditioning, daily pre-hab work, nutrition, and recovery protocols."
    },
    {
      icon: Zap,
      title: "PERFORMANCE",
      description: "Building functional muscle and optimal body composition for peak athletic output."
    },
    {
      icon: Heart,
      title: "HEALTH",
      description: "Monitor sleep, heart rate variability, and key metrics to ensure peak performance."
    }
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  // Athletic training video URLs (royalty-free)
  const videoUrls = [
    "https://videos.pexels.com/video-files/3195396/3195396-uhd_3840_2160_25fps.mp4", // Runner training
    "https://videos.pexels.com/video-files/4761437/4761437-uhd_2560_1440_25fps.mp4", // Gym workout
  ];

  return (
    <div className="min-h-screen bg-[#070B14] text-white overflow-x-hidden">
      {/* Navigation */}
      <PublicNavbar transparent={true} />

      {/* ==================== HERO SECTION ==================== */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        {/* Video Background */}
        <div className="absolute inset-0 z-0">
          <video
            ref={videoRef}
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover"
            poster="https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=1920&q=80"
          >
            <source src="https://videos.pexels.com/video-files/4761437/4761437-hd_1920_1080_25fps.mp4" type="video/mp4" />
          </video>
          {/* Dark Overlay */}
          <div className="absolute inset-0 bg-[#070B14]/50"></div>
          {/* Gradient Overlay */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#070B14] via-transparent to-[#070B14]/30"></div>
        </div>

        {/* Hero Content */}
        <div className="relative z-10 w-full max-w-[1800px] mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, ease: "easeOut" }}
          >
            {/* Massive Typography */}
            <h1 className="font-black text-[12vw] md:text-[10vw] lg:text-[9vw] leading-[0.85] tracking-[-0.02em] uppercase">
              <span className="block bg-gradient-to-r from-white via-white to-blue-200 bg-clip-text text-transparent">PALADIN</span>
            </h1>
          </motion.div>
        </div>

        {/* Bottom Content */}
        <div className="absolute bottom-0 left-0 right-0 z-10">
          <div className="max-w-[1800px] mx-auto px-6 md:px-12 pb-12">
            {/* Tagline */}
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5, duration: 1 }}
              className="text-sm md:text-base tracking-[0.3em] uppercase text-white/80 mb-8"
            >
              Elite Strength & Conditioning
            </motion.p>

            {/* Scroll Indicator */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
              className="flex flex-col items-center"
            >
              <span className="text-xs tracking-[0.2em] uppercase text-white/60 mb-3">Scroll to view more</span>
              <motion.div
                animate={{ y: [0, 8, 0] }}
                transition={{ repeat: Infinity, duration: 1.5 }}
              >
                <ChevronDown className="w-5 h-5 text-white/60" />
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ==================== ABOUT SECTION ==================== */}
      <section id="about" className="py-32 px-6 md:px-12 bg-[#070B14]">
        <div className="max-w-[1800px] mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Left - Section Label */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <span className="text-blue-400 text-sm tracking-[0.3em] uppercase">01 // About Us</span>
              <h2 className="text-5xl md:text-6xl lg:text-7xl font-black tracking-tight mt-6 mb-8 leading-[0.9]">
                WORLD-CLASS<br />
                <span className="text-blue-400/50">TRAINING</span>
              </h2>
              <p className="text-white/60 text-lg leading-relaxed max-w-xl">
                We build elite athletes through AI-powered programming, 
                scientific methodology, and relentless pursuit of excellence. 
                Every rep counts. Every day matters.
              </p>
            </motion.div>

            {/* Right - Image */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              className="relative"
            >
              <img 
                src="https://images.unsplash.com/photo-1534438327276-14e5300c3a48?w=800&q=80"
                alt="Training"
                className="w-full aspect-[4/3] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/50 to-transparent"></div>
            </motion.div>
          </div>

          {/* Stats Row */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-24 pt-16 border-t border-white/10"
          >
            {[
              { value: "10K+", label: "Athletes Trained" },
              { value: "500+", label: "Programs" },
              { value: "98%", label: "Success Rate" },
              { value: "24/7", label: "AI Support" }
            ].map((stat, i) => (
              <div key={i}>
                <p className="text-4xl md:text-5xl font-black">{stat.value}</p>
                <p className="text-sm text-white/40 uppercase tracking-[0.2em] mt-2">{stat.label}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ==================== PROGRAMS SECTION ==================== */}
      <section id="programs" className="py-32 px-6 md:px-12 bg-gradient-to-b from-[#070B14] to-[#0a1020]">
        <div className="max-w-[1800px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <span className="text-blue-400 text-sm tracking-[0.3em] uppercase">02 // Programs</span>
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-black tracking-tight mt-6 leading-[0.9]">
              CHOOSE YOUR<br />
              <span className="text-blue-400/50">PATH</span>
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {programs.map((program, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className={`relative bg-gradient-to-b from-white/5 to-transparent border rounded-2xl p-8 md:p-10 group hover:border-blue-500/30 transition-all duration-500 ${
                  program.popular 
                    ? "border-blue-500/50 shadow-xl shadow-blue-500/10" 
                    : "border-white/10"
                }`}
                data-testid={`program-card-${program.tier}`}
              >
                {program.popular && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-gradient-to-r from-blue-600 to-blue-400 text-white px-4 py-1 rounded-full text-xs font-bold tracking-wider uppercase">
                    Most Popular
                  </div>
                )}
                
                <h3 className="text-2xl font-black tracking-tight mb-1">{program.title}</h3>
                <p className="text-white/40 text-sm mb-8">{program.subtitle}</p>
                
                <div className="flex items-baseline gap-1 mb-10">
                  <span className="text-5xl md:text-6xl font-black">{program.price}</span>
                  <span className="text-white/40">{program.interval}</span>
                </div>
                
                <ul className="space-y-4 mb-10">
                  {program.features.map((feature, j) => (
                    <li key={j} className="flex items-start gap-3">
                      <Check className={`w-4 h-4 mt-1 flex-shrink-0 ${program.popular ? "text-blue-400" : "text-green-500"}`} />
                      <span className="text-white/70 text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <button
                  onClick={() => navigate("/pricing")}
                  className={`w-full py-4 rounded-full font-bold tracking-[0.15em] uppercase text-sm transition-all ${
                    program.popular
                      ? "bg-gradient-to-r from-blue-600 to-blue-500 text-white hover:from-blue-500 hover:to-blue-400 shadow-lg shadow-blue-500/25"
                      : "bg-white/10 text-white hover:bg-white/20"
                  }`}
                  data-testid={`program-cta-${program.tier}`}
                >
                  Get Started
                </button>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== PHILOSOPHY SECTION ==================== */}
      <section className="py-32 px-6 md:px-12 bg-[#070B14] relative overflow-hidden">
        {/* Background Video */}
        <div className="absolute inset-0 z-0 opacity-30">
          <video
            autoPlay
            muted
            loop
            playsInline
            className="w-full h-full object-cover"
          >
            <source src="https://videos.pexels.com/video-files/3195396/3195396-hd_1920_1080_25fps.mp4" type="video/mp4" />
          </video>
        </div>
        <div className="absolute inset-0 bg-[#070B14]/70 z-0"></div>

        <div className="max-w-[1800px] mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center max-w-4xl mx-auto"
          >
            <span className="text-amber-400 text-sm tracking-[0.3em] uppercase">The Paladin Philosophy</span>
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-black tracking-tight mt-6 mb-8 leading-[0.9]">
              HEALTH FIRST.<br />
              PERFORMANCE ALWAYS.
            </h2>
            <p className="text-white/60 text-lg leading-relaxed mb-12">
              We build systems that allow high performance to be expressed repeatedly, 
              safely, and progressively over time.
            </p>
            <Link 
              to="/philosophy"
              className="inline-flex items-center gap-3 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-black px-8 py-4 rounded-full font-bold tracking-[0.15em] uppercase text-sm transition-all"
              data-testid="philosophy-link"
            >
              Read The Doctrine
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>

          {/* 4 Pillars */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mt-24">
            {keyComponents.map((component, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-[#070B14]/50 backdrop-blur-sm border border-white/10 rounded-2xl p-8 hover:border-amber-500/30 transition-all duration-500"
                data-testid={`component-card-${i}`}
              >
                <component.icon className="w-8 h-8 text-amber-400 mb-6" />
                <h3 className="text-lg font-bold tracking-tight mb-3">{component.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{component.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ==================== RESULTS SECTION ==================== */}
      <section id="results" className="py-32 px-6 md:px-12 bg-gradient-to-b from-[#0a1020] to-[#070B14]">
        <div className="max-w-[1800px] mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-16"
          >
            <span className="text-green-400 text-sm tracking-[0.3em] uppercase">03 // Results</span>
            <h2 className="text-5xl md:text-6xl lg:text-7xl font-black tracking-tight mt-6 leading-[0.9]">
              WHAT OUR<br />
              <span className="text-green-400/50">ATHLETES SAY</span>
            </h2>
          </motion.div>

          {/* Testimonial */}
          <div className="max-w-4xl">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeTestimonial}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.5 }}
                className="border-l-2 border-blue-500/50 pl-8 md:pl-12"
              >
                <blockquote className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-8">
                  "{testimonials[activeTestimonial].quote}"
                </blockquote>
                <div className="flex items-center gap-4">
                  <img 
                    src={testimonials[activeTestimonial].avatar}
                    alt={testimonials[activeTestimonial].name}
                    className="w-14 h-14 object-cover"
                  />
                  <div>
                    <p className="font-bold">{testimonials[activeTestimonial].name}</p>
                    <p className="text-white/50 text-sm">{testimonials[activeTestimonial].role}</p>
                  </div>
                </div>
              </motion.div>
            </AnimatePresence>

            {/* Dots */}
            <div className="flex gap-3 mt-12">
              {testimonials.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActiveTestimonial(i)}
                  className={`h-1 transition-all ${
                    i === activeTestimonial ? "bg-blue-500 w-12" : "bg-white/20 w-8 hover:bg-white/40"
                  }`}
                  data-testid={`testimonial-dot-${i}`}
                />
              ))}
            </div>
          </div>

          {/* Results Grid */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-24 pt-16 border-t border-white/10"
          >
            {[
              { sport: "Basketball", improvement: "+6\" Vertical" },
              { sport: "Football", improvement: "+40lbs Squat" },
              { sport: "Soccer", improvement: "2x Speed" },
              { sport: "MMA", improvement: "+15lbs Lean" }
            ].map((result, i) => (
              <div key={i} className="text-center p-6 bg-white/5 rounded-xl border border-white/10">
                <p className="text-3xl md:text-4xl font-black text-blue-400 mb-2">{result.improvement}</p>
                <p className="text-white/40 text-sm uppercase tracking-[0.2em]">{result.sport}</p>
              </div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ==================== FINAL CTA ==================== */}
      <section className="py-32 px-6 md:px-12 bg-[#070B14] relative overflow-hidden">
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 via-transparent to-amber-500/20"></div>
        </div>
        
        <div className="max-w-[1800px] mx-auto relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center"
          >
            <h2 className="text-6xl md:text-7xl lg:text-8xl font-black tracking-tight mb-8 leading-[0.9]">
              READY?
            </h2>
            <p className="text-white/60 text-lg mb-12 max-w-xl mx-auto">
              Join thousands of athletes training with AI-powered precision. 
              Your journey starts now.
            </p>
            <Link 
              to="/pricing"
              className="inline-flex items-center gap-3 bg-gradient-to-r from-blue-600 to-blue-500 text-white px-10 py-5 rounded-full font-bold tracking-[0.15em] uppercase text-sm hover:from-blue-500 hover:to-blue-400 transition-all shadow-2xl shadow-blue-500/30"
              data-testid="cta-start-now"
            >
              Start Training Today
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Legal Footer */}
      <LegalFooter />
    </div>
  );
};

export default HomePage;
